import axios from 'axios';

// Ce fichier configure l'instance axios utilisée par toute l'application.
// Il ajoute :
// - l'URL de base de l'API
// - la gestion du cache pour les requêtes GET
// - l'ajout du token d'authentification
// - le rafraîchissement en arrière-plan des réponses stale
// - la gestion des erreurs réseau et 401.

// Use environment variable or default to localhost for development
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Warn if using localhost in production
if (
  API_URL.includes('localhost') &&
  window.location.hostname !== 'localhost' &&
  window.location.hostname !== '127.0.0.1'
) {
  /* console.error removed */
  // Show alert to user
  alert(
    'CONFIGURATION ERROR:\n\n' +
    'The app is trying to connect to localhost but is running on Vercel.\n\n' +
    'You need to set the REACT_APP_API_URL environment variable in your Vercel project settings.\n\n' +
    'Example: REACT_APP_API_URL=https://your-backend.onrender.com'
  );
}

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  /* console.log removed */
}

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 60000, // 60 second timeout for all requests (increase to accommodate larger uploads)
});

const responseCache = new Map();
const inflightRequests = new Map();

const buildCacheKey = (url, config = {}) => {
  const paramsPart = config?.params ? JSON.stringify(config.params) : '';
  const tokenPart = localStorage.getItem('user_token') || '';
  return `${url}::${paramsPart}::${tokenPart.slice(-16)}`;
};

api.getCached = (url, config = {}, ttlMs = 30000) => {
  if (!ttlMs || ttlMs <= 0) {
    return api.get(url, config);
  }

  const cacheKey = buildCacheKey(url, config);
  const now = Date.now();
  const cachedEntry = responseCache.get(cacheKey);
  const staleWindowMs = Math.max(ttlMs, 30000);

  if (cachedEntry && cachedEntry.expiresAt > now) {
    return Promise.resolve(cachedEntry.response);
  }

  // Stale-while-revalidate: return stale data quickly while refreshing in background.
  if (cachedEntry && (cachedEntry.expiresAt + staleWindowMs) > now) {
    if (!inflightRequests.has(cacheKey)) {
      const refreshPromise = api
        .get(url, config)
        .then((response) => {
          responseCache.set(cacheKey, {
            response,
            expiresAt: Date.now() + ttlMs,
          });
          return response;
        })
        .finally(() => {
          inflightRequests.delete(cacheKey);
        });
      inflightRequests.set(cacheKey, refreshPromise);
    }
    return Promise.resolve(cachedEntry.response);
  }

  if (inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey);
  }

  const requestPromise = api
    .get(url, config)
    .then((response) => {
      responseCache.set(cacheKey, {
        response,
        expiresAt: Date.now() + ttlMs,
      });
      return response;
    })
    .finally(() => {
      inflightRequests.delete(cacheKey);
    });

  inflightRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

api.invalidateCache = (prefix = '') => {
  if (!prefix) {
    responseCache.clear();
    inflightRequests.clear();
    return;
  }

  [...responseCache.keys()].forEach((key) => {
    if (key.startsWith(prefix)) responseCache.delete(key);
  });
  [...inflightRequests.keys()].forEach((key) => {
    if (key.startsWith(prefix)) inflightRequests.delete(key);
  });
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('user_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Enhanced error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ERR_NETWORK') {
      /* console.error removed */
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('user_token');
      localStorage.removeItem('user_data');
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

api.interceptors.response.use((response) => {
  const method = (response?.config?.method || '').toLowerCase();
  if (method && method !== 'get') {
    api.invalidateCache('/evaluations/catalog');
    api.invalidateCache('/users/my-attempts');
  }
  return response;
});

export default api;
